import React, { ChangeEvent, useEffect, useMemo, useReducer } from 'react'
import { TextInput, TextInputIconType } from 'components/atoms/TextInput'
import { ISelectOption } from 'components/forms/Selects'
import sortBy from 'lodash/sortBy'
import uniqBy from 'lodash/uniqBy'
import { matchSorter } from 'match-sorter'
import { FilterAutocomplete } from 'transfix-ui/components/FilterAutocomplete'
import { SingleSelect } from 'transfix-ui/components/SingleSelect'
import { makeStyles } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useTeamMemberContext } from 'apps/fleet-planner/context/TeamMemberContext'
import { IMotorCarrierDetail, TEAM_MEMBER_USER_TYPES } from 'apps/fleet-planner/types'
import { DryVanIcon, UserIcon } from 'transfix-icons'

const useTeamMemberTableFiltersStyles = (isMobile?: boolean) =>
  makeStyles(({ spacing }) => ({
    root: {
      display: 'flex',
      gap: spacing(2),
      flexWrap: 'wrap',
      alignItems: 'center',
      marginLeft: isMobile ? undefined : spacing(4),
    },
    textFilter: {
      maxWidth: isMobile ? undefined : 325,
    },
  }))

interface SearchFilter {
  query: string | null
  carrierId: number | null
  userType: TEAM_MEMBER_USER_TYPES | null
}

interface SearchUpdate {
  query?: string | null
  carrierId?: number | null
  userType?: TEAM_MEMBER_USER_TYPES | null
  clear?: boolean
}

const DEFAULT_SEARCH_STATE: SearchFilter = {
  query: null,
  carrierId: null,
  userType: null,
}

const ROLE_FILTER_OPTIONS: ISelectOption[] = [
  {
    label: 'Dispatcher',
    value: 'dispatcher',
  },
  {
    label: 'Driver',
    value: 'driver',
  },
  {
    label: 'Owner Operator',
    value: 'owner_operator',
  },
]

function searchReducer(filter: SearchFilter, update: SearchUpdate) {
  if (update.query !== undefined) {
    filter = { ...filter, query: update.query }
  }
  if (update.carrierId !== undefined) {
    filter = { ...filter, carrierId: update.carrierId }
  }
  if (update.userType !== undefined) {
    filter = { ...filter, userType: update.userType }
  }
  if (update.clear) {
    filter = DEFAULT_SEARCH_STATE
  }
  return filter
}

export const TeamMemberTableFilters = () => {
  const { isMobile } = useBreakpointContext()
  const classes = useTeamMemberTableFiltersStyles(isMobile)()
  const { teamMembers = [], setFilteredTeamMembers } = useTeamMemberContext()
  const [searchState, dispatch] = useReducer(searchReducer, DEFAULT_SEARCH_STATE)

  const availableCarriers = useMemo(() => {
    return sortBy(
      uniqBy(
        teamMembers
          .map(tm => {
            const { motorCarrierDetails = [] } = tm || {}
            return motorCarrierDetails
          })
          .flat(),
        'motorCarrierId'
      ),
      'legalName'
    )
  }, [teamMembers])

  const carrier = availableCarriers.find(
    ({ motorCarrierId }) => searchState.carrierId === motorCarrierId
  )

  const carrierFilterChangeHandler = (carrier: IMotorCarrierDetail | any) => {
    dispatch({ carrierId: carrier ? carrier.motorCarrierId : null })
  }
  const userTypeChangeHandler = (status?: ISelectOption) =>
    dispatch({
      userType: status?.value ? (String(status.value) as TEAM_MEMBER_USER_TYPES) : null,
    })
  const queryChangeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
    dispatch({
      query: value || null,
    })

  const searchPhone = (phoneType: string) => {
    return isMobile ? '' : { threshold: matchSorter.rankings.STARTS_WITH, key: phoneType }
  }

  // Updates filteredTeamMembers when filters are selected
  useEffect(() => {
    let filtered = [...teamMembers]
    // filter team members on search query
    if (searchState.query) {
      filtered = matchSorter(teamMembers, searchState.query, {
        keys: [
          'name',
          'firstName',
          'lastName',
          ...[searchPhone('phone')],
          ...[searchPhone('cellPhone')],
        ],
      })
    }

    filtered = filtered.filter(tm => {
      const { motorCarrierDetails = [] } = tm || {}
      // filter on carrier name
      if (
        searchState.carrierId &&
        !motorCarrierDetails.some(mc => mc.motorCarrierId === searchState.carrierId)
      ) {
        return false
      }
      // filter on user type
      if (
        searchState.userType &&
        !motorCarrierDetails.some(mc => mc.userType === searchState.userType)
      ) {
        return false
      }
      return true
    })

    // return list of carriers sorted by last name
    filtered = sortBy(filtered, tm => tm.lastName.toLowerCase())
    setFilteredTeamMembers(filtered)
  }, [teamMembers, searchState, setFilteredTeamMembers])

  const searchBy = isMobile
    ? 'Search team members by name or email'
    : 'Search by name, email, or phone'

  return (
    <div className={classes.root}>
      <TextInput
        placeholder={searchBy}
        className={classes.textFilter}
        icon={TextInputIconType.search}
        onChange={queryChangeHandler}
        large
      />
      <FilterAutocomplete
        id='carrier-filter-autocomplete'
        dataTest='equipment-type'
        label='Carriers'
        placeholder='Search'
        icon={<DryVanIcon size='large' />}
        options={availableCarriers}
        value={carrier}
        setValue={carrierFilterChangeHandler}
        getOptionLabel={(mcd: IMotorCarrierDetail) => mcd.legalName}
        getOptionSelected={(a: IMotorCarrierDetail, b: IMotorCarrierDetail) =>
          a?.motorCarrierId === b?.motorCarrierId
        }
        quickSuggest
        autoSelect={false}
      />
      <SingleSelect
        dataTest='role-select-filter'
        id='role-select-filter'
        label='Role'
        icon={<UserIcon size='large' />}
        options={ROLE_FILTER_OPTIONS}
        onSelect={userTypeChangeHandler}
        selected={searchState.userType || ''}
      />
    </div>
  )
}
